export class ErrorHelper {
  /**
   * Prepends a custom message to an HTTP error's detail.
   *
   * Useful for adding context to the user when an API error message
   * doesn’t clearly indicate which operation failed.
   *
   * Example:
   *   "Employee save failed. <original error message>"
   */
  public static prefixWithMessage<T extends { error?: { detail?: string } }>(
    message: string,
    error: T
  ): T {
    if (error?.error?.detail) {
      error.error.detail = `${message} ${error.error.detail}`;
    }

    return error;
  }
}

/**
 * Extracts the user-facing message from a failed HTTP call, falling back to a
 * caller-supplied generic string.
 *
 * The shared server exception handler (UpsGlobalExceptionHandler) returns
 * RFC-7807 ProblemDetails, so a mapped error (e.g. a ValidationException such as
 * the blocked-phase guard, #30262) carries its human-readable reason in
 * camelCase `detail`. Some older endpoints send a Pascal `Message`, a few send a
 * bare string body, and legacy ones a `messages` string array (joined with
 * `|`), so all shapes are checked in that order. Anything
 * empty, whitespace-only, or non-string yields the fallback so a malformed
 * error body never renders as an empty toast or "undefined".
 *
 * Consolidates the ad-hoc extractors previously copied into
 * track-reconciliation (`apiErrorMessage`) and the Time Manager import service
 * (`extractErrorDetail`); those call sites can adopt this shared helper.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const body = (err as { error?: unknown } | null | undefined)?.error;

  if (typeof body === 'string') {
    return body.trim().length > 0 ? body : fallback;
  }

  const problem = body as
    | {
        detail?: unknown;
        Detail?: unknown;
        message?: unknown;
        Message?: unknown;
      }
    | null
    | undefined;

  const candidate =
    problem?.detail ?? problem?.Detail ?? problem?.message ?? problem?.Message;

  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    return candidate;
  }

  const messages = (body as { messages?: unknown } | null | undefined)
    ?.messages;
  const joined = Array.isArray(messages)
    ? messages
        .filter(
          (m): m is string => typeof m === 'string' && m.trim().length > 0
        )
        .join('|')
    : '';

  return joined.length > 0 ? joined : fallback;
}
